import { Link } from 'react-router-dom';
import { t, tn } from '@app/i18n';
import { Card, InfoTip } from '@zarya/ui';
import type { Overview } from './Overview.api';
import { cardFailure } from './degradation';
import s from './Overview.module.css';

/**
 * Card **Atividade 24 h** da segunda linha de T3 (Lote 2b): o que o tenant fez no
 * dia — decisões ALLOW e DENY, aprovações que saíram da fila e releases vistas.
 *
 * **Os quatro números falam da MESMA janela**, e é isso que o card existe para
 * garantir. `window_minutes` vem do servidor junto com os números, e é ele que
 * escreve o «24 h» do título: mudar a janela no backend e deixar o rótulo para trás
 * é como se escreve uma tela que mente sem ninguém errar uma linha.
 *
 * Fontes:
 *
 *   - ALLOW, DENY e o total vêm **contados pelo veredito**, de UMA fonte só — o
 *     histórico persistido quando ele já é autoridade, o agregado por tool do
 *     executor (L14b) até lá. Quem escolhe é o backend (`fillDeny24h`), e escolhe
 *     para os três juntos: um número de cada fonte daria populações e janelas
 *     diferentes, e a conta sairia inflada ou negativa;
 *   - aprovações decididas e releases vistas são leitura LOCAL (Postgres), e por isso
 *     o card desenha inteiro na abertura, enquanto a leitura nova do cluster acontece
 *     atrás.
 *
 * **Este comentário já disse que «ALLOW é a subtração», e estava errado** — errado do
 * lado permissivo, que é o que uma tela de segurança não pode errar. `total − DENY`
 * é permitido MAIS indeterminado. Fica registrado porque foi o comentário que
 * sustentou o engano: ele descrevia a consistência da conta, não o significado dela.
 */

interface StatProps {
  label: string;
  /** `null` = não foi possível ler. Nunca desenhado como zero. */
  value: number | null | undefined;
  detail: string;
  to: string;
  tip: string;
}

/**
 * O ⓘ fica **fora** do `<Link>`, e isso não é detalhe de estilo: `InfoTip` renderiza
 * um `<button>`, e botão dentro de âncora é HTML inválido — o navegador reaninha a
 * árvore, o teclado passa a ter dois destinos no mesmo alvo e o leitor de tela lê o
 * texto do ⓘ como parte do nome do link («ALLOW Sobre ALLOW 124 decisões
 * permitidas…»). Rótulo + ⓘ ficam na linha de cima; o link envolve só o que leva à
 * outra tela.
 */
function Stat({ label, value, detail, to, tip }: StatProps) {
  const unread = value === null || value === undefined;
  return (
    <div className={s.atividadeItem}>
      <span className={s.atividadeRotulo}>
        {label}
        <InfoTip text={tip} label={t('overview.activity.tipLabel', { label })} size="xs" />
      </span>
      <Link className={s.atividadeLink} to={to} aria-label={label}>
        <strong className={s.atividadeValor}>{unread ? '—' : value}</strong>
        <span className={s.atividadeDetalhe}>
          {unread ? t('overview.activity.unread') : detail}
        </span>
      </Link>
    </div>
  );
}

export interface ActivityCardProps {
  overview: Overview;
}

export function ActivityCard({ overview }: ActivityCardProps) {
  const { indicators, activity, failures } = overview;
  const hours = Math.round((activity?.window_minutes ?? 1440) / 60);

  // **ALLOW é LIDO, não derivado.** Até 09/09 este card fazia `decisões − DENY`, e
  // isso conta o INDETERMINADO — a decisão que o PDP não conseguiu avaliar — como
  // permitida. Numa tela de segurança o erro tem lado, e aquele era o permissivo.
  // Agora os três vêm contados pelo veredito, da mesma fonte e da mesma janela
  // (`fillDeny24h` no backend decide store × L14b para os três juntos).
  //
  // A única subtração que sobra é a do indeterminado, e ela é honesta: é o que
  // ninguém rotulou de ALLOW nem de DENY.
  const failureDecisions = cardFailure(failures, 'deny_24h');
  const total = indicators.decisions_24h;
  const denied = indicators.deny_24h;
  const allowed = indicators.allow_24h;
  const known = (value: number | null | undefined): value is number =>
    !failureDecisions && value !== null && value !== undefined;
  const undetermined =
    known(total) && known(denied) && known(allowed) ? total - denied - allowed : null;

  return (
    <Card
      title={t('overview.activity.title', { hours })}
      tip={t('overview.activity.tip', { hours })}
      aria-label={t('overview.activity.title', { hours })}
    >
      <div className={s.atividade}>
        <Stat
          label={t('overview.activity.allow')}
          value={known(allowed) ? allowed : null}
          detail={t('overview.activity.allow.detail')}
          to="/decisoes"
          tip={t('overview.activity.allow.tip')}
        />
        <Stat
          label={t('overview.activity.deny')}
          value={known(denied) ? denied : null}
          detail={t('overview.activity.deny.detail')}
          to="/decisoes"
          tip={t('overview.activity.deny.tip')}
        />
        <Stat
          label={t('overview.activity.approvals')}
          value={activity?.approvals_decided}
          detail={tn('overview.activity.approvals.detail', activity?.approvals_decided ?? 0)}
          to="/aprovacoes"
          tip={t('overview.activity.approvals.tip')}
        />
        <Stat
          label={t('overview.activity.releases')}
          value={activity?.releases_seen}
          detail={tn('overview.activity.releases.detail', activity?.releases_seen ?? 0)}
          to="/identidades"
          tip={t('overview.activity.releases.tip')}
        />
      </div>

      {/* O indeterminado só aparece quando existe. Zero indeterminadas é o caso
          normal, e uma linha dizendo «0 indeterminadas» em toda abertura de T3 é
          ruído que ensina a não ler o card. Quando é > 0, porém, é a diferença
          entre «tudo foi decidido» e «o PDP não conseguiu avaliar n» — e é
          justamente essa parcela que o ALLOW absorvia antes. */}
      {undetermined !== null && undetermined > 0 ? (
        <p className={s.indeterminadas}>{tn('overview.activity.undetermined', undetermined)}</p>
      ) : null}
    </Card>
  );
}
